import { Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { RateCard } from '@shared/models/rate-card.model';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
  ConfirmationDialogResult
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  getRateBlockName,
  getRateBlockOptions,
  RATE_BLOCK_CHANGE_WARNING,
  RateBlockOption,
  RateBlockSelection,
  supportsRateBlockSelection
} from '@shared/utils/rate-block.util';

/**
 * CR 5205: lets the user pick which contract rate block (vehicle type & size) the rates are taken from.
 * Rendered only for FLAG_DOWN / COMMITMENT contracts. Every user change is confirmed with the billing-impact
 * warning; `selectionChange` is emitted only on "Yes", "No" restores the previous value.
 */
@Component({
  selector: 'hmt-rate-block-select',
  templateUrl: './rate-block-select.component.html',
  styleUrls: ['./rate-block-select.component.scss'],
  standalone: false
})
export class RateBlockSelectComponent implements OnChanges {
  @Input() rateCard: RateCard | null = null;
  /** Block the system matched for the load (null when nothing matched). */
  @Input() autoKey: string | null = null;
  /** Currently applied block; falls back to autoKey. */
  @Input() value: string | null = null;
  @Input() required = false;
  @Input() disabled = false;
  @Input() label = 'Vehicle Type & Size';
  @Input() placeholder = 'Select vehicle type & size';
  /**
   * form: outlined hmt-form-field (dialogs); pill: compact rounded select (table rows);
   * pill-dark: compact rounded select on a dark toolbar.
   */
  @Input() variant: 'form' | 'pill' | 'pill-dark' = 'form';
  /** Keep the (disabled) select on screen when the contract has no selectable blocks / no contract yet. */
  @Input() showWhenUnavailable = false;
  /** The block (or the contract it belongs to) is being saved / loaded: show a spinner and block changes. */
  @Input() loading = false;

  @Output() selectionChange = new EventEmitter<RateBlockSelection>();

  options: RateBlockOption[] = [];
  selectedKey: string | null = null;

  constructor(private dialog: MatDialog) {}

  @HostBinding('class')
  get hostClass(): string {
    return `rate-block-host rate-block-host--${this.variant}`;
  }

  /** The contract offers selectable blocks (FLAG_DOWN / COMMITMENT). */
  get available(): boolean {
    return supportsRateBlockSelection(this.rateCard);
  }

  get visible(): boolean {
    return this.available || this.showWhenUnavailable;
  }

  get selectDisabled(): boolean {
    return this.disabled || !this.available || this.loading;
  }

  get missingSelection(): boolean {
    return this.required && this.available && !this.loading && !this.selectedKey;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rateCard) {
      this.options = getRateBlockOptions(this.rateCard);
    }
    if (changes.rateCard || changes.value || changes.autoKey) {
      this.selectedKey = this.value ?? this.autoKey ?? null;
    }
  }

  onSelect(event: MatSelectChange): void {
    const previousKey = this.selectedKey;
    const newKey: string = event.value;
    if (newKey === previousKey) {
      return;
    }
    const data: ConfirmationDialogData = {
      message: RATE_BLOCK_CHANGE_WARNING,
      imagePath: 'assets/icons/defaultIcon.png',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    };
    this.dialog
      .open(ConfirmationDialogComponent, { width: '800px', data })
      .afterClosed()
      .subscribe((result: ConfirmationDialogResult) => {
        if (result?.buttonPressed === 'yes') {
          this.selectedKey = newKey;
          this.selectionChange.emit({
            selectedRateBlockKey: newKey,
            selectedRateBlockName: getRateBlockName(this.rateCard, newKey),
            autoMatchedRateBlockKey: this.autoKey ?? null
          });
        } else {
          this.selectedKey = previousKey;
          event.source.value = previousKey;
        }
      });
  }
}
